import {
  DataTableProps,
  SimplePagination,
  SimplePaginationProps,
  SortStatus,
  Table,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import type {
  ConversationAndRequester,
  ReadConversationsApiArg,
  ReadConversationsSortKey,
} from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { readConversationSortKeys } from 'apis/conversations';
import { UsePaginatedResult } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { getSortObjectFromString } from 'utils/url/getSortObjectFromString';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ConversationsTableConversationPlaceholderRows } from './components/rows/conversationPlaceholderRows/conversationsTableConversationPlaceholderRows';
import { ConversationsTableConversationRow } from './components/rows/conversationRow/conversationsTableConversationRow';
import { ConversationsTableEmptyRow } from './components/rows/emptyRow/conversationsTableEmptyRow';
import { ConversationsTableHeadRow } from './components/rows/headRow/conversationsTableHeadRow';
import { ConversationsTableRejectedRow } from './components/rows/rejectedRow/conversationsTableRejectedRow';
import styles from './conversationsTable.module.scss';

export type ConversationsTableProps = Pick<
  UsePaginatedResult<ConversationAndRequester, ReadConversationsApiArg, never>,
  'asyncStatus' | 'items'
> &
  PropsWithClassName &
  Required<
    Pick<DataTableProps<ConversationAndRequester, never>, 'selectableRowsProps'>
  > & {
    paginationProps?: SimplePaginationProps;
  };

/** Represents the conversations table component. */
export const ConversationsTable = ({
  items,
  asyncStatus,
  selectableRowsProps,
  paginationProps,
}: ConversationsTableProps): JSX.Element => {
  const { hasBulkOperations } = useFeatureFlag(['has-bulk-operations']);
  const [isInitialized, setIsInitialized] = useState(false);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const {
    params: { sort },
    setParam,
    unsetParam,
  } = useAppSearchParams('/conversations');
  const [sortStatus, setSortStatus] = useState<SortStatus | null>(null);

  const scrollTableToTop = useCallback(() => {
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollTop = 0;
    }
  }, []);

  const handleTableSortChange = useCallback(
    (newSortStatus: SortStatus | null) => {
      setSortStatus(newSortStatus);

      if (newSortStatus) {
        setParam('sort', Object.values(newSortStatus).join(':'));
      } else {
        unsetParam('sort');
      }
    },
    [setParam, unsetParam],
  );

  const tableBody = useMemo(() => {
    if (asyncStatus === 'uninitialized' || asyncStatus === 'pending') {
      return <ConversationsTableConversationPlaceholderRows />;
    }

    if (asyncStatus === 'rejected') {
      return <ConversationsTableRejectedRow />;
    }

    if (!items.length) {
      return <ConversationsTableEmptyRow />;
    }

    return (
      <>
        {items.map((conversationAndRequester) => (
          <ConversationsTableConversationRow
            key={conversationAndRequester.conversation.id}
            conversationAndRequester={conversationAndRequester}
          />
        ))}
      </>
    );
  }, [items, asyncStatus]);

  // Handles scrolling to the top of the table when there are new items.
  useEffect(() => {
    if (asyncStatus === 'fulfilled' || asyncStatus === 'rejected') {
      scrollTableToTop();

      // Tells that the state has been through the first cycle.
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }
  }, [asyncStatus, isInitialized, scrollTableToTop]);

  // Handles settings the sort status by reading the sort search param.
  useEffectOnce(() => {
    if (!sort) {
      // Because the default sorting of conversations and created_at can be
      // considered the same we only set the sorting state on the column and
      // don't do any fetching of data until the user interacts with one of the
      // sorting columns.
      setSortStatus({
        sortBy: 'created_at' as const satisfies ReadConversationsSortKey,
        direction: 'desc',
      });
    } else {
      const sortObject = getSortObjectFromString(
        sort,
        readConversationSortKeys,
      );

      if (sortObject && sortObject.direction) {
        setSortStatus({
          sortBy: sortObject.key,
          direction: sortObject.direction,
        });
      }
    }
  });

  return (
    <div className={styles.root}>
      <Table
        stickyHeader
        hoverable={asyncStatus === 'fulfilled' && items.length > 0}
        loading={asyncStatus === 'uninitialized' || asyncStatus === 'pending'}
        loadingStrategy="disabled"
        wrapperRef={tableWrapperRef}
        className={classnames(styles.table, {
          [styles.tableExpanded]:
            (asyncStatus === 'fulfilled' && !items.length) ||
            asyncStatus === 'rejected',
        })}
        selectableRowsProps={
          items.length && hasBulkOperations ? selectableRowsProps : undefined
        }
        sortingProps={{
          sortStatus,
          onSortChange: handleTableSortChange,
          sortableColumns: [
            'last_message_created_at',
            'created_at',
          ] satisfies ReadConversationsSortKey[],
        }}
      >
        <Table.Thead>
          <ConversationsTableHeadRow isInitialized={isInitialized} />
        </Table.Thead>

        <Table.Tbody>{tableBody}</Table.Tbody>

        <Table.Footer>
          <SimplePagination {...paginationProps} />
        </Table.Footer>
      </Table>
    </div>
  );
};
